import React, { useEffect, useMemo, useState } from "react";
import { X, Trash2, Plus, Minus, ShoppingBag, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/upr-api";
import { useCart } from "@/contexts/CartContext";

// Persist the last-picked country locally so returning customers don't have
// to re-select it on every visit.
const COUNTRY_KEY = "upr:ship_country";

export default function CartDrawer() {
  const { items, open, setOpen, setQuantity, remove, subtotal, currency, count } = useCart();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [country, setCountry] = useState(() => {
    try { return localStorage.getItem(COUNTRY_KEY) || ""; } catch { return ""; }
  });
  const [shipping, setShipping] = useState({ rates: null, countries: [] });

  // Load shipping rates + curated country list once when the drawer first opens.
  useEffect(() => {
    if (!open || shipping.rates) return;
    api.get("/shipping/rates")
      .then(({ data }) => setShipping({ rates: data.rates, countries: data.countries || [] }))
      .catch(() => {});
  }, [open, shipping.rates]);

  useEffect(() => {
    try { if (country) localStorage.setItem(COUNTRY_KEY, country); } catch { /* ignore */ }
  }, [country]);

  // Digital-only carts skip shipping entirely (server enforces the same rule).
  const allDigital = items.length > 0 && items.every((l) => (l.category || "").toLowerCase() === "digital");

  const shippingLine = useMemo(() => {
    if (allDigital) return { cost: 0, label: "Digital only — no shipping", needsCountry: false };
    if (!shipping.rates) return { cost: 0, label: "", needsCountry: true };
    const c = (shipping.countries || []).find((x) => x.code === country);
    if (!c) return { cost: 0, label: "", needsCountry: true };
    const zone = shipping.rates[c.zone] || {};
    const price = Number(zone.price || 0);
    const freeOver = Number(zone.free_over || 0);
    if (freeOver > 0 && subtotal >= freeOver) {
      return { cost: 0, label: `Free ${zone.label} shipping`, needsCountry: false };
    }
    return { cost: price, label: `${zone.label} shipping`, needsCountry: false };
  }, [allDigital, shipping, country, subtotal]);

  const total = useMemo(
    () => Math.max(0, subtotal + (shippingLine.cost || 0)),
    [subtotal, shippingLine.cost],
  );

  const checkout = async () => {
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!allDigital && !country) { toast.error("Please pick a shipping country"); return; }
    setBusy(true);
    try {
      const body = {
        items: items.map((l) => ({ merch_id: l.merch_id, quantity: l.quantity, variant_label: l.variant_label || undefined })),
        origin_url: window.location.origin,
      };
      if (email.trim()) body.email = email.trim();
      if (code.trim()) body.code = code.trim().toUpperCase();
      if (!allDigital) body.country = country;
      const { data } = await api.post("/checkout/cart", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="cart-drawer">
      <button
        type="button"
        aria-label="Close cart"
        onClick={() => setOpen(false)}
        className="flex-1 bg-black/40 backdrop-blur-sm"
        data-testid="cart-overlay"
      />
      <aside
        className="w-full max-w-md h-full flex flex-col"
        style={{ background: "var(--upr-bg)", borderLeft: "1px solid var(--upr-border)" }}
      >
        <header className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--upr-border)" }}>
          <div className="font-heading font-extrabold text-xl flex items-center gap-2">
            <ShoppingBag size={18} /> Cart <span style={{ color: "var(--upr-muted)" }}>· {count}</span>
          </div>
          <button onClick={() => setOpen(false)} className="upr-btn upr-btn-ghost" data-testid="cart-close"><X size={14} /></button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="text-sm py-12 text-center" style={{ color: "var(--upr-muted)" }}>
              Your cart is empty. <br />
              <span className="text-xs">Hit the shop and pick a vinyl or two.</span>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((l) => (
                <li key={`${l.merch_id}::${l.variant_label}`} className="flex gap-3" data-testid={`cart-line-${l.merch_id}-${l.variant_label || "none"}`}>
                  <div className="w-16 h-16 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}>
                    {l.image_url && <img src={l.image_url} alt={l.merch_name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold leading-tight truncate">{l.merch_name}</div>
                    <div className="text-xs" style={{ color: "var(--upr-muted)" }}>
                      {l.variant_label && <>Size <strong>{l.variant_label}</strong> · </>}{l.currency} {Number(l.price).toFixed(2)}
                      {l.has_wav && (
                        <span className="inline-flex items-center gap-0.5 ml-1" style={{ color: "var(--upr-primary)" }}>
                          · <Download size={9} /> MP3
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        className="upr-btn upr-btn-ghost"
                        onClick={() => setQuantity(l.merch_id, l.variant_label, l.quantity - 1)}
                        disabled={l.quantity <= 1}
                        style={{ padding: "0.2rem 0.4rem" }}
                        data-testid={`cart-line-qty-minus-${l.merch_id}`}
                      ><Minus size={11} /></button>
                      <span className="font-heading text-sm" data-testid={`cart-line-qty-${l.merch_id}`}>{l.quantity}</span>
                      <button
                        type="button"
                        className="upr-btn upr-btn-ghost"
                        onClick={() => setQuantity(l.merch_id, l.variant_label, l.quantity + 1)}
                        disabled={l.quantity >= 10}
                        style={{ padding: "0.2rem 0.4rem" }}
                        data-testid={`cart-line-qty-plus-${l.merch_id}`}
                      ><Plus size={11} /></button>
                      <button
                        type="button"
                        className="ml-auto upr-btn upr-btn-ghost"
                        onClick={() => remove(l.merch_id, l.variant_label)}
                        data-testid={`cart-line-remove-${l.merch_id}`}
                      ><Trash2 size={11} /></button>
                    </div>
                  </div>
                  <div className="font-heading font-extrabold text-sm self-start">
                    {l.currency} {(l.price * l.quantity).toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <footer className="px-5 py-4 space-y-3" style={{ borderTop: "1px solid var(--upr-border)" }}>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="email"
                placeholder="Email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="cart-email"
                className="p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
                style={{ borderColor: "var(--upr-border)" }}
              />
              <input
                type="text"
                placeholder="Code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                data-testid="cart-code"
                className="p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm uppercase tracking-widest"
                style={{ borderColor: "var(--upr-border)" }}
              />
            </div>

            {!allDigital && (
              <div>
                <label htmlFor="cart-country" className="upr-eyebrow block mb-1">Ship to</label>
                <select
                  id="cart-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="cart-country"
                  className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
                  style={{ borderColor: "var(--upr-border)" }}
                >
                  <option value="">— pick a country —</option>
                  {shipping.countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-1" data-testid="cart-totals">
              <div className="flex items-baseline justify-between text-sm" style={{ color: "var(--upr-muted)" }}>
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{currency} {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between text-sm" style={{ color: "var(--upr-muted)" }}>
                <span>
                  Shipping
                  {shippingLine.label && <span className="ml-1 text-xs">· {shippingLine.label}</span>}
                </span>
                <span data-testid="cart-shipping">
                  {shippingLine.needsCountry ? "—" : `${currency} ${shippingLine.cost.toFixed(2)}`}
                </span>
              </div>
              <div className="flex items-baseline justify-between pt-1">
                <div className="upr-eyebrow">Total</div>
                <div className="font-heading font-extrabold text-2xl" data-testid="cart-total">
                  {currency} {total.toFixed(2)}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="upr-btn w-full"
              onClick={checkout}
              disabled={busy || (!allDigital && !country)}
              data-testid="cart-checkout"
            >
              {busy ? "…" : "Checkout"}
            </button>
            <p className="text-xs" style={{ color: "var(--upr-muted)" }}>
              Secure payment via Stripe. Any release-linked items deliver high-quality MP3 downloads on the receipt page.
            </p>
          </footer>
        )}
      </aside>
    </div>
  );
}
